/**
 * Funzioni agnostiche sulle identità — elenco canonico dei resolver di
 * identità e gate sulle foglie letterali.
 *
 * Regola concordata: le funzioni NON nominano identità (fornitore, insegna,
 * canale, marchio prodotto, listino). Le identità sono parametri della testata
 * gara (fornitore) o del perimetro del configuratore, applicati dal motore in
 * ammissione prima di qualsiasi funzione. Le differenziazioni per identità
 * ("7 % se ZTE, 4 % altrimenti") si fanno con due configuratori con perimetri
 * diversi, non con una funzione.
 *
 * Una foglia è "letterale di identità" quando confronta una variabile il cui
 * `resolver_key` è in questo elenco con un VALORE o una LISTA (value, values,
 * listRefId). Restano ammessi: confronto variabile-variabile (es. fornitore
 * del prodotto = fornitore del listino) e foglie di stato (presente/assente).
 */
import type {
  CommissioningKernelCompareLeaf, CommissioningKernelNode, CommissioningKernelSetLeaf,
} from '../types/commissioning-kernel';

export const COMMISSIONING_IDENTITY_RESOLVER_KEYS = [
  // Fornitore (documento sell-in, prodotto, listino) e nome fornitore.
  'sellin.supplier_id',
  'sellin.supplier_name',
  'product.supplier_id',
  'priceListItem.supplier_id',
  // Marchio prodotto (pos_brands).
  'product.brand_id',
  // Insegna / canale: prodotto, listino, vendita, sell-in.
  'product.retail_brand_id',
  'priceListItem.retail_brand_id',
  'priceListItem.channel_ids',
  'sale.retail_brand_id',
  'sale.channel_id',
  'sellin.retail_brand_id',
  'sellin.channel_id',
  // Id listino.
  'priceListItem.price_list_id',
] as const;
export type CommissioningIdentityResolverKey = typeof COMMISSIONING_IDENTITY_RESOLVER_KEYS[number];

const IDENTITY_RESOLVER_SET: ReadonlySet<string> = new Set(COMMISSIONING_IDENTITY_RESOLVER_KEYS);

export function isCommissioningIdentityResolverKey(resolverKey: string): resolverKey is CommissioningIdentityResolverKey {
  return IDENTITY_RESOLVER_SET.has(resolverKey);
}

/** Etichetta mostrata sulle foglie legacy (funzioni già pubblicate). */
export const COMMISSIONING_IDENTITY_LITERAL_LABEL = 'identità da portare a parametro';

/** Foglia letterale di identità trovata nell'AST. */
export interface CommissioningIdentityLiteralLeaf {
  /** Riferimento variabile `@codice`. */
  variable: string;
  resolverKey: CommissioningIdentityResolverKey;
  leaf: CommissioningKernelCompareLeaf | CommissioningKernelSetLeaf;
}

/** La foglia confronta la variabile con un valore/lista (non var-var, non unaria pura)? */
export function commissioningLeafHasLiteralOperand(
  leaf: CommissioningKernelCompareLeaf | CommissioningKernelSetLeaf,
): boolean {
  if (leaf.kind === 'condition') {
    if (leaf.rightVariable !== undefined) return false;
    return leaf.value !== undefined || leaf.values !== undefined || leaf.listRefId !== undefined;
  }
  return (leaf.values !== undefined && leaf.values.length > 0) || leaf.listRefId !== undefined;
}

/** Codice variabile (senza `@`) di un riferimento `@codice`. */
function variableCodeOf(variable: string): string {
  return variable.startsWith('@') ? variable.slice(1) : variable;
}

/**
 * Walk condiviso dell'AST: visita le foglie compare/set_op con operando
 * letterale. I `func_ref` NON vengono attraversati: la funzione referenziata
 * è già passata dal gate alla sua pubblicazione.
 */
function visitLiteralLeaves(
  node: CommissioningKernelNode,
  onLeaf: (leaf: CommissioningKernelCompareLeaf | CommissioningKernelSetLeaf) => void,
): void {
  const visit = (n: CommissioningKernelNode): void => {
    switch (n.kind) {
      case 'condition':
      case 'set_op':
        if (commissioningLeafHasLiteralOperand(n)) onLeaf(n);
        return;
      case 'and':
      case 'or':
        n.children.forEach(visit);
        return;
      case 'not':
        visit(n.child);
        return;
      case 'state':
      case 'func_ref':
        return;
    }
  };
  visit(node);
}

/**
 * Id delle funzioni referenziate (`func_ref`) da un AST, senza attraversarle:
 * walk puro condiviso da motore (kernel) e preflight per la chiusura delle
 * funzioni di un'istanza.
 */
export function commissioningCollectFunctionRefs(
  node: CommissioningKernelNode, into: Set<string> = new Set<string>(),
): Set<string> {
  switch (node.kind) {
    case 'func_ref':
      into.add(node.functionId);
      break;
    case 'and':
    case 'or':
      for (const child of node.children) commissioningCollectFunctionRefs(child, into);
      break;
    case 'not':
      commissioningCollectFunctionRefs(node.child, into);
      break;
    default:
      break;
  }
  return into;
}

/**
 * Codici (senza `@`) delle variabili confrontate con un operando letterale:
 * sono le sole da risolvere a catalogo per decidere se la foglia nomina
 * un'identità.
 */
export function commissioningCollectLiteralLeafVariableCodes(
  nodes: readonly CommissioningKernelNode[],
  into: Set<string> = new Set<string>(),
): Set<string> {
  for (const node of nodes) visitLiteralLeaves(node, (leaf) => { into.add(variableCodeOf(leaf.variable)); });
  return into;
}

/**
 * Foglie compare/set_op su variabili di identità con operando letterale.
 * `resolverKeyByCode` mappa codice variabile (senza `@`) → resolver_key;
 * variabili sconosciute non sono considerate (il gate di coerenza le gestisce
 * a parte).
 */
export function commissioningCollectIdentityLiteralLeaves(
  node: CommissioningKernelNode,
  resolverKeyByCode: ReadonlyMap<string, string>,
): CommissioningIdentityLiteralLeaf[] {
  const out: CommissioningIdentityLiteralLeaf[] = [];
  visitLiteralLeaves(node, (leaf) => {
    const resolverKey = resolverKeyByCode.get(variableCodeOf(leaf.variable));
    if (resolverKey !== undefined && isCommissioningIdentityResolverKey(resolverKey)) {
      out.push({ variable: leaf.variable, resolverKey, leaf });
    }
  });
  return out;
}
